import { Skeleton } from '@/components/ui/skeleton';
import { MarkdownRenderer } from '@/features/accountability/components/MarkdownRenderer';
import { useInvoiceMemory } from '../hooks/useInvoiceMemory';

interface InvoiceMemoryTabProps {
  invoiceNumber: string;
  enabled: boolean;
}

export function InvoiceMemoryTab({ invoiceNumber, enabled }: InvoiceMemoryTabProps) {
  const { data, isLoading, isError, refetch } = useInvoiceMemory(invoiceNumber, enabled);

  if (isLoading) {
    return (
      <div aria-busy="true" className="mt-4 space-y-2">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="mt-4 space-y-3">
        <p className="text-sm text-destructive">Failed to load AI memory for this invoice.</p>
        <button
          type="button"
          onClick={() => refetch()}
          className="rounded-md border border-border bg-background px-3 py-1.5 text-sm text-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          Try again
        </button>
      </div>
    );
  }

  if (data?.threadExists === false || !data?.workingMemory?.trim()) {
    return (
      <div className="mt-4 flex items-center justify-center border rounded-lg p-6">
        <p className="text-sm text-muted-foreground">No AI memory recorded for this invoice yet.</p>
      </div>
    );
  }

  return (
    <div className="mt-4 overflow-y-auto max-h-[60vh] border rounded-lg p-4 bg-card">
      <MarkdownRenderer>{data.workingMemory}</MarkdownRenderer>
    </div>
  );
}
